/**
 * Builds lease reassessment / modification journal entry lines by comparing
 * assessment schedule (prior month closing) vs reassessment or modification output (event month opening).
 */

export type JournalEntryResult =
  | {
      ok: true;
      sourceLabel: "Reassessment" | "Modification";
      eventMonth: string;
      priorMonth: string;
      closingPriorAssessment: number;
      openingEvent: number;
      delta: number;
      amount: number;
    }
  | { ok: false; message: string };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatMonthYear(d: Date): string {
  const m = MONTHS[d.getMonth()];
  const y = d.getFullYear() % 100;
  return `${m}-${String(y).padStart(2, "0")}`;
}

export function parseMonthLabel(label: string): Date | null {
  const s = (label || "").trim();
  const m = s.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (!m) return null;
  const monthIdx = MONTHS.findIndex((x) => x.toLowerCase() === m[1].toLowerCase());
  if (monthIdx < 0) return null;
  const yy = parseInt(m[2], 10);
  const year = 2000 + yy;
  return new Date(year, monthIdx, 1);
}

export function prevMonthLabel(monthLabel: string): string | null {
  const d = parseMonthLabel(monthLabel);
  if (!d) return null;
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return formatMonthYear(p);
}

export function nextMonthLabel(monthLabel: string): string | null {
  const d = parseMonthLabel(monthLabel);
  if (!d) return null;
  const n = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return formatMonthYear(n);
}

function parseMmDdYyyy(s: string): Date | null {
  const t = (s || "").trim();
  const parts = t.split("/");
  if (parts.length !== 3) return null;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return null;
  return new Date(yyyy, mm - 1, dd);
}

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return NaN;
}

export function findLeaseByName(leases: unknown, name: string): Record<string, unknown> | null {
  if (!Array.isArray(leases)) return null;
  const n = String(name || "").trim();
  for (const L of leases) {
    if (!L || typeof L !== "object") continue;
    const li = (L as Record<string, unknown>).lease_info;
    if (li && typeof li === "object") {
      const nm = String((li as Record<string, unknown>)["Name of Lease"] ?? "").trim();
      if (nm === n) return L as Record<string, unknown>;
    }
  }
  return null;
}

/** Latest commencement date wins; only considers files that contain the lease. */
export function pickReassessmentOrModification(
  leaseName: string,
  reassData: unknown,
  modData: unknown,
): { which: "reassessment" | "modification"; lease: Record<string, unknown> } | null {
  const rLease = findLeaseByName((reassData as Record<string, unknown>)?.leases, leaseName);
  const mLease = findLeaseByName((modData as Record<string, unknown>)?.leases, leaseName);

  const rDateStr =
    rLease && (rLease.lease_info as Record<string, unknown>)?.["Lease Commencement Date"];
  const mDateStr =
    mLease && (mLease.lease_info as Record<string, unknown>)?.["Lease Commencement Date"];

  const rD = typeof rDateStr === "string" ? parseMmDdYyyy(rDateStr) : null;
  const mD = typeof mDateStr === "string" ? parseMmDdYyyy(mDateStr) : null;

  if (!rLease && !mLease) return null;
  if (rLease && !mLease) return { which: "reassessment", lease: rLease };
  if (!rLease && mLease) return { which: "modification", lease: mLease };

  const rTime = rD ? rD.getTime() : 0;
  const mTime = mD ? mD.getTime() : 0;
  if (mTime > rTime) return { which: "modification", lease: mLease! };
  return { which: "reassessment", lease: rLease! };
}

export function computeJournalEntry(
  leaseName: string,
  assessmentData: unknown,
  reassData: unknown,
  modData: unknown,
): JournalEntryResult {
  const picked = pickReassessmentOrModification(leaseName, reassData, modData);
  if (!picked) {
    return {
      ok: false,
      message:
        "No reassessment or modification output found for this lease. Run Modification or Reassessment and ensure the lease name matches.",
    };
  }

  const aLease = findLeaseByName((assessmentData as Record<string, unknown>)?.leases, leaseName);
  if (!aLease) {
    return { ok: false, message: "Lease not found in Execution_LEASE_AssessmentOut.json. Run Lease Testing first." };
  }

  const chosenSchedule = picked.lease.schedule;
  if (!Array.isArray(chosenSchedule) || chosenSchedule.length === 0) {
    return { ok: false, message: "Selected reassessment/modification output has no schedule." };
  }

  const firstRow = chosenSchedule[0] as Record<string, unknown>;
  const eventMonth = String(firstRow["Month"] ?? "").trim();
  if (!eventMonth) {
    return { ok: false, message: "Could not read event month from reassessment/modification schedule." };
  }

  const priorMonth = prevMonthLabel(eventMonth);
  if (!priorMonth) {
    return { ok: false, message: `Could not compute month before "${eventMonth}".` };
  }

  const aSchedule = aLease.schedule;
  if (!Array.isArray(aSchedule)) {
    return { ok: false, message: "Assessment output has no schedule." };
  }

  let closingPrior: number | null = null;
  for (const row of aSchedule) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    if (String(r["Month"] ?? "").trim() === priorMonth) {
      closingPrior = num(r["Closing Balance"]);
      break;
    }
  }

  if (closingPrior === null || Number.isNaN(closingPrior)) {
    return {
      ok: false,
      message: `No assessment row for month "${priorMonth}" (month before event "${eventMonth}"). Check Assessment Out matches this lease timeline.`,
    };
  }

  const openingEvent = num(firstRow["Opening Balance"]);
  if (Number.isNaN(openingEvent)) {
    return { ok: false, message: "Could not read Opening Balance from reassessment/modification first schedule row." };
  }

  const delta = openingEvent - closingPrior;
  const amount = Math.abs(Math.round(delta));

  if (amount === 0) {
    return {
      ok: true,
      sourceLabel: picked.which === "reassessment" ? "Reassessment" : "Modification",
      eventMonth,
      priorMonth,
      closingPriorAssessment: closingPrior,
      openingEvent,
      delta,
      amount: 0,
    };
  }

  return {
    ok: true,
    sourceLabel: picked.which === "reassessment" ? "Reassessment" : "Modification",
    eventMonth,
    priorMonth,
    closingPriorAssessment: closingPrior,
    openingEvent,
    delta,
    amount,
  };
}

/** Same delta as reassessment journal, but only uses Execution_LEASE_Modification.json (no reassessment file). */
export function computeAssessmentVsModificationDelta(
  leaseName: string,
  assessmentData: unknown,
  modificationData: unknown,
): JournalEntryResult {
  const picked = findLeaseByName((modificationData as Record<string, unknown>)?.leases, leaseName);
  if (!picked) {
    return {
      ok: false,
      message:
        "Lease not found in Execution_LEASE_Modification.json. Run Lease Modification and ensure the lease name matches.",
    };
  }

  const aLease = findLeaseByName((assessmentData as Record<string, unknown>)?.leases, leaseName);
  if (!aLease) {
    return { ok: false, message: "Lease not found in Execution_LEASE_AssessmentOut.json. Run Lease Testing first." };
  }

  const chosenSchedule = picked.schedule;
  if (!Array.isArray(chosenSchedule) || chosenSchedule.length === 0) {
    return { ok: false, message: "Modification output has no schedule." };
  }

  const firstRow = chosenSchedule[0] as Record<string, unknown>;
  const eventMonth = String(firstRow["Month"] ?? "").trim();
  if (!eventMonth) {
    return { ok: false, message: "Could not read event month from modification schedule." };
  }

  const priorMonth = prevMonthLabel(eventMonth);
  if (!priorMonth) {
    return { ok: false, message: `Could not compute month before "${eventMonth}".` };
  }

  const aSchedule = aLease.schedule;
  if (!Array.isArray(aSchedule)) {
    return { ok: false, message: "Assessment output has no schedule." };
  }

  let closingPrior: number | null = null;
  for (const row of aSchedule) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    if (String(r["Month"] ?? "").trim() === priorMonth) {
      closingPrior = num(r["Closing Balance"]);
      break;
    }
  }

  if (closingPrior === null || Number.isNaN(closingPrior)) {
    return {
      ok: false,
      message: `No assessment row for month "${priorMonth}" (month before event "${eventMonth}").`,
    };
  }

  const openingEvent = num(firstRow["Opening Balance"]);
  if (Number.isNaN(openingEvent)) {
    return { ok: false, message: "Could not read Opening Balance from modification first schedule row." };
  }

  const delta = openingEvent - closingPrior;
  const amount = Math.abs(Math.round(delta));

  if (amount === 0) {
    return {
      ok: true,
      sourceLabel: "Modification",
      eventMonth,
      priorMonth,
      closingPriorAssessment: closingPrior,
      openingEvent,
      delta,
      amount: 0,
    };
  }

  return {
    ok: true,
    sourceLabel: "Modification",
    eventMonth,
    priorMonth,
    closingPriorAssessment: closingPrior,
    openingEvent,
    delta,
    amount,
  };
}
